import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import { gwStorefrontAppInfoToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { eventsCollectorToken } from "@modules/analytics_events_collector/di_tokens";
import { CommonModule } from "@modules/common_module/CommonModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { filter, lastValueFrom, take, tap } from "rxjs";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import {
    NudgesApplicationConfigInterface,
    NudgesApplicationConfigSchema,
} from "./NudgesApplicationConfig";
import NudgesSettingsQuery from "./queries/NudgesSettingsQuery";
import NudgesSettingsService from "./services/NudgesSettingsService";
import {
    NudgesApplicationConfigToken,
    nudgesApplicationLoggerToken,
    nudgesAuthApiClientToken,
} from "./tokens";
import NudgesWidgetInjector from "./widgets/NudgesWidgetInjector";

@singleton()
export class NudgesApplication {
    constructor(
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(NudgesApplicationConfigToken)
        public readonly config: NudgesApplicationConfigInterface,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector,
        @inject(TokenManagerModule)
        private readonly tokenManagerModule: TokenManagerModule
    ) {
        assert(config, NudgesApplicationConfigSchema);
    }

    init(): this {
        const logger = new MinilogLogger("nudges");
        container.registerInstance(nudgesApplicationLoggerToken, logger);

        const eventsCollector = container.resolve(eventsCollectorToken);
        const analyticsEventsFactory = container.resolve(
            AnalyticsEventsFactory
        );

        container.register(nudgesAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/v3/nudges"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(nudgesApplicationLoggerToken)
                        ),
                        eventsCollector,
                        container.resolve(
                            CommonModule
                        ).exports.locationController,
                        analyticsEventsFactory
                    ),
                    this.tokenManagerModule.exports.tokenManager
                );
            }),
        });

        const nudgesSettingsService = container.resolve(NudgesSettingsService);
        void lastValueFrom(nudgesSettingsService.isAllowed());

        const nudgesSettingsQuery = container.resolve(NudgesSettingsQuery);

        const isAllowed$ = nudgesSettingsQuery.isAllowed$;

        // Injector
        const nudgesWidgetInjector = new NudgesWidgetInjector(
            this.config.nudgesWidgetPlaceholderSelector,
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.mutationObserverInjector
        );

        isAllowed$
            .pipe(
                filter((isAllowed) => isAllowed),
                take(1),
                tap(() => nudgesWidgetInjector.start())
            )
            .subscribe();

        return this;
    }
}
